import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { showToast } from "../../../../core/utils/alert";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { useSelector } from "react-redux";
import {
  selectUserRole,
  selectCurrentUser,
} from "../../../../core/data/slice/authSlice";
import {
  useImportStudentsMutation,
  useLazyDownloadStudentImportTemplateQuery,
} from "../api/studentImportApi";

const ImportStudents = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<
    "idle" | "reading" | "uploading" | "processing"
  >("idle");

  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const centerId = currentUser?.center_id;

  // API hooks
  const [importStudents] = useImportStudentsMutation();
  const [triggerDownload, { isLoading: isDownloading }] =
    useLazyDownloadStudentImportTemplateQuery();

  const getDashboardRoute = () => {
    switch (userRole) {
      case "superadmin":
        return all_routes.adminDashboard;
      case "centeradmin":
        return all_routes.centerDashboard;
      default:
        return all_routes.adminDashboard;
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileSelect = (file: File | undefined) => {
    if (!file) {
      showToast({
        title: "Error",
        text: "No file selected",
        icon: "error",
      });
      return;
    }

    // Check if file is excel
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (validTypes.includes(file.type)) {
      setSelectedFile(file);
    } else {
      showToast({
        title: "Error",
        text: "Please upload only Excel files (.xls or .xlsx)",
        icon: "error",
      });
    }
  };

  // Handle template download
  const handleDownloadSample = async () => {
    try {
      const blob = await triggerDownload(Date.now(), false).unwrap();

      if (blob) {
        // Create a download link and click it
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "student-import-template.xlsx";
        document.body.appendChild(a);
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        showToast({
          title: "Success",
          text: "Template downloaded successfully",
          icon: "success",
        });
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      showToast({
        title: "Error",
        text: "Failed to download template file",
        icon: "error",
      });
    }
  };

  // Handle import students
  const handleImportStudents = async () => {
    if (!selectedFile) {
      showToast({
        title: "Warning",
        text: "Please select a file to import",
        icon: "warning",
      });
      return;
    }

    // if (!centerId) {
    //   showToast({
    //     title: "Error",
    //     text: "Center ID is required",
    //     icon: "error",
    //   });
    //   return;
    // }

    setIsUploading(true);

    // Simulate file reading
    setUploadStage("reading");
    setUploadProgress(10);

    setTimeout(() => {
      // Simulate upload starting
      setUploadStage("uploading");
      setUploadProgress(30);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prevProgress) => {
          const newProgress = prevProgress + 10;
          if (newProgress >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return newProgress;
        });
      }, 500);

      // Actual API call
      setTimeout(async () => {
        try {
          setUploadStage("processing");

          const response = await importStudents({
            file: selectedFile,
            // center_id: centerId,
          }).unwrap();

          setUploadProgress(100);

          // Reset after showing 100% completion
          setTimeout(() => {
            setIsUploading(false);
            setUploadProgress(0);
            setUploadStage("idle");

            if (response.success) {
              showToast({
                title: "Success",
                text: `Successfully imported students`,
                icon: "success",
              });
            } else {
              showToast({
                title: "Warning",
                text: `Import completed with issues.`,
                icon: "warning",
              });
            }
          }, 1000);
        } catch (error) {
          const apiError = error as any;
          console.error("Error importing students:", apiError);
          setIsUploading(false);
          setUploadProgress(0);
          setUploadStage("idle");

          let errorMessage = getApiErrorMessage(apiError);
          let errorTitle = "Error";

          if (apiError?.data?.status === 422) {
            errorTitle = "Validation Error";
            errorMessage = `Please check the validation of all rows and then try again.`;
          }

          showToast({
            title: errorTitle,
            text: errorMessage,
            icon: "error",
          });
        }
      }, 2000);
    }, 1000);
  };

  // Get stage display text
  const getStageText = () => {
    switch (uploadStage) {
      case "reading":
        return "Reading file...";
      case "uploading":
        return "Uploading file...";
      case "processing":
        return "Processing data...";
      default:
        return "";
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Import Students</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={getDashboardRoute()}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.studentList}>Students</Link>
                </li>
                <li className="breadcrumb-item active">Import Students</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="mb-2">
              <button
                className="btn btn-primary d-flex align-items-center"
                onClick={handleDownloadSample}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Downloading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-download me-2"></i> Download Sample
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        {/* /Page Header */}

        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">Upload Excel File</h4>
              </div>
              <div className="card-body">
                {/* Upload Section or Progress Bar */}
                <div className="mb-4">
                  {isUploading ? (
                    <div className="upload-progress p-4">
                      <div className="text-center mb-3">
                        <div className="upload-icon mb-3">
                          {uploadProgress < 100 ? (
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : (
                            <i className="ti ti-check-circle fs-1 text-success"></i>
                          )}
                        </div>
                        <h5>{getStageText()}</h5>
                        <p className="text-muted mb-0">
                          Please wait while we process your file
                        </p>
                      </div>

                      <div className="progress mb-2" style={{ height: "10px" }}>
                        <div
                          className={`progress-bar progress-bar-striped progress-bar-animated bg-${uploadProgress === 100 ? "success" : "primary"
                            }`}
                          role="progressbar"
                          style={{ width: `${uploadProgress}%` }}
                          aria-valuenow={uploadProgress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        ></div>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span className="text-muted small">
                          Uploading your file...
                        </span>
                        <span className="fw-medium">{uploadProgress}%</span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`upload-area border-2 border-dashed rounded-3 p-4 text-center ${isDragging ? "bg-light-300" : ""
                        }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <div className="upload-area-icon mb-3">
                        <i className="ti ti-file-upload fs-1 text-primary"></i>
                      </div>
                      <h6>Drag and drop your Excel file here</h6>
                      <p className="text-muted mb-3">or</p>
                      <label className="btn btn-primary mb-3">
                        <input
                          type="file"
                          hidden
                          accept=".xls,.xlsx"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            handleFileSelect(file);
                          }}
                        />
                        Browse File
                      </label>
                      <p className="text-muted small mb-0">
                        Supported formats: .xls, .xlsx (Max size: 5MB)
                      </p>
                    </div>
                  )}
                </div>

                {/* Selected File Info - Only show when not uploading */}
                {selectedFile && !isUploading && (
                  <div className="selected-file bg-light p-3 rounded-3 mb-4">
                    <div className="d-flex align-items-center">
                      <i className="ti ti-file-spreadsheet fs-4 text-primary me-2"></i>
                      <div className="flex-grow-1">
                        <h6 className="mb-1">{selectedFile.name}</h6>
                        <p className="text-muted small mb-0">
                          {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        className="btn btn-sm btn-icon btn-light"
                        onClick={() => setSelectedFile(null)}
                      >
                        <i className="ti ti-x"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="import-instructions bg-light-200 p-3 rounded-3">
                  <h6 className="mb-3">Important Instructions:</h6>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="ti ti-point text-primary me-2"></i>
                      Make sure to follow the sample Excel format
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-point text-primary me-2"></i>
                      All required fields must be filled
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-point text-primary me-2"></i>
                      Date format should be YYYY-MM-DD
                    </li>
                    <li>
                      <i className="ti ti-point text-primary me-2"></i>
                      Maximum 500 records can be imported at once
                    </li>
                  </ul>
                </div>
              </div>
              <div className="card-footer text-end">
                <Link
                  to={all_routes.studentList}
                  className="btn btn-light me-2"
                >
                  Cancel
                </Link>
                <button
                  className="btn btn-primary"
                  disabled={!selectedFile || isUploading}
                  onClick={handleImportStudents}
                >
                  {isUploading ? "Processing..." : "Import Students"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportStudents;
